import { useCallback, useState, useRef, useEffect } from "react";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import IconButton from "@mui/material/IconButton";
import Fade from "@mui/material/Fade";
import Stack from "@mui/material/Stack";

interface SearchToggleProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  placeholder?: string;
  width?: number;
}

export const SearchToggle = ({
  searchTerm,
  onSearchChange,
  placeholder = "Search...",
  width = 250,
}: SearchToggleProps) => {
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSearchToggle = useCallback(() => {
    setShowSearch(!showSearch);
    if (showSearch) {
      // Clear search when hiding
      onSearchChange("");
    }
  }, [showSearch, onSearchChange]);

  // Focus input when search becomes visible
  useEffect(() => {
    if (showSearch) {
      // Small delay to ensure the input is fully visible after Fade animation
      const timer = setTimeout(() => {
        const input = inputRef.current?.querySelector("input");
        if (input) {
          input.focus();
        }
      }, 350);

      return () => clearTimeout(timer);
    }
  }, [showSearch]);

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
      width={width}
      height={40}
      position="relative"
    >
      <Fade in={!showSearch} timeout={300}>
        <IconButton
          onClick={handleSearchToggle}
          size="small"
          sx={{ position: "absolute", right: 0 }}
        >
          <SearchIcon />
        </IconButton>
      </Fade>

      <Fade in={showSearch} timeout={300}>
        <TextField
          ref={inputRef}
          variant="standard"
          size="small"
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{
            position: "absolute",
            right: 0,
            width,
            opacity: showSearch ? 1 : 0,
            pointerEvents: showSearch ? "auto" : "none",
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          onBlur={() => {
            if (!searchTerm.trim()) {
              setShowSearch(false);
            }
          }}
        />
      </Fade>
    </Stack>
  );
};
